import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import { ready } from 'shared/ready'
import emailSpellChecker from '../support/checkEmail'

const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]
const MAX_SIZE = 10 * 1024 * 1024 // 10MB in bytes
const FLOW = 'one-screen'

class UploadModalSettings {
  constructor() {
    this.fileData = undefined

    this.$modalUploadContainer = document.getElementById('upload-modal-container_v1')
    // just to confirm that the v1 is the one being used.
    if (!this.$modalUploadContainer) return

    this.$emailSubmitBtn = document.querySelector('#modal_upload_v1_form button[type="submit"]')

    this.$formV1 = document.getElementById('modal_upload_v1_form')
    this.$fileButton = document.getElementById('modal_upload__button-v1-retry')
    this.$uploadSubtitle = document.getElementById('upload_modal_v1_subtitle')

    this.$inputField = document.getElementById('modalUploadEmailInput')
    this.$fileField = document.getElementById('modalUploadFileInput')
    this.$submitButton = document.getElementById('modal_upload_button_v1')

    this.fillEventListeners()
    this.fillHomepageEventListeners()
  }

  openModal() {
    this.$modalUploadContainer.classList.add('is-animated', 'is-visible')
  }

  closeModal() {
    this.$modalUploadContainer.classList.remove('is-animated', 'is-visible')
  }

  checkFile(file) {
    if (!file) return { success: false, message: 'no file' }
    if (!ALLOWED_TYPES.includes(file.type))
      return { success: false, message: 'file not valid type' }
    if (file.size > MAX_SIZE) return { success: false, message: 'file over 10mb' }

    return { success: true }
  }

  resetUploadModalSettings() {
    trackInternalEvent('click_exit_upload_modal', {
      ...this.getLabelAndBlock(),
      flow: FLOW,
    })
    this.fileData = undefined
    this.closeModal()
  }

  async sendUploadDetails(e) {
    e.preventDefault()
    this.clearEmailError()
    const email = e.target.email.value || undefined

    try {
      // call the api to create the email
      const createUserResponse = await this.createUser(email)

      if (createUserResponse.suggestion) {
        this.emailSuggestion = createUserResponse.suggestion
        this.showEmailErrorMessage('email_suggestion', {
          suggestion: createUserResponse.suggestion,
        })
        return false
      }

      this.emailSuggestion = null

      // If user already exists, show a message with a link to sign-in page
      if (
        !createUserResponse.success &&
        createUserResponse.error === 'User already exists' &&
        (createUserResponse.userExists || createUserResponse.user_exists)
      ) {
        return this.showEmailErrorMessage('email_taken', {})
      }

      if (createUserResponse.error === 'email_invalid') {
        return this.showEmailErrorMessage('email_invalid', {})
      }

      // If other error, show error modal
      if (!createUserResponse.success) return false

      trackMarketingEvent('Sign up', 'Upload CTA', { flow: FLOW })

      // call the api to upload
      const uploadResponse = await this.uploadCV(email)
      if (!uploadResponse) return false

      // all good call the api to upload the data associated to the email.
      window.location.replace(uploadResponse)
    } catch (error) {
      // TODO: show error here
      return false
    }
  }

  /* API calls */
  async createUser(email) {
    this.$emailSubmitBtn.disabled = true
    if (!this.emailSuggestion) {
      const suggestedEmail = await emailSpellChecker.run({ email })
      if (suggestedEmail) {
        this.$emailSubmitBtn.disabled = false
        return { suggestion: suggestedEmail.full }
      }
    }

    const response = await fetch('/api/builder/resume_upload/register', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
      }),
    })
    this.$emailSubmitBtn.disabled = false
    const data = await response.json()
    return {
      success: response.status === 200,
      ...data,
    }
  }

  async uploadCV(email = null) {
    const form = new FormData()
    form.append('resume', this.fileData)

    // If email is provided, we're creating a new user
    // Otherwise upload directly
    if (email) {
      form.append('email', email)
    }

    trackInternalEvent('submit_sign_up_upload', { ...this.getLabelAndBlock(), flow: FLOW })

    const response = await fetch('/api/builder/resume_upload/upload_and_parse', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: form,
    })

    if (response.status !== 200) return false
    const data = await response.json()

    return data.redirectUrl
  }

  /* Prepare event listeners */
  fillEventListeners() {
    document
      .getElementById('modal__close-upload')
      .addEventListener('click', this.resetUploadModalSettings.bind(this))
    this.$formV1 && this.$formV1.addEventListener('submit', this.sendUploadDetails.bind(this))

    this.$fileButton.addEventListener('click', e => {
      e.preventDefault()
      this.$fileField.click()
    })

    this.$fileField.addEventListener('change', e => {
      const file = e.target.files[0]

      if (file) {
        const { success, message } = this.checkFile(file)
        this.fileData = file

        if (success) {
          this.$uploadSubtitle.classList.remove('error')
          this.$uploadSubtitle.innerText = 'DOC, DOCX, or PDF (max 10MB)'
          this.$fileButton.classList.add('with_file')
          this.$fileButton.innerText = file.name

          // notify of the upload resume error
          trackInternalEvent('upload_resume_success', {
            ...this.getLabelAndBlock(),
            flow: FLOW,
          })
        } else {
          this.$uploadSubtitle.classList.add('error')
          this.$uploadSubtitle.innerText = 'It should be less than 10MB in DOC, DOCX, or PDF'
          this.$fileButton.classList.remove('with_file')
          this.$fileButton.innerText = 'Browse file'

          // notify of the upload resume error
          trackInternalEvent('upload_resume_error', {
            ...this.getLabelAndBlock(),
            error: message || '',
            flow: FLOW,
          })
        }
      }
    })

    this.$formV1.addEventListener('change', e => this.checkChangeFields(e))
    this.$inputField.addEventListener('input', e => this.checkChangeFields(e))
  }

  checkChangeFields(e) {
    const target = e.target
    const email = this.$inputField.value
    const file = this.$fileField.value

    if (email && file) {
      this.$submitButton.classList.remove('disabled')
    } else {
      this.$submitButton.classList.add('disabled')
    }

    if (target.id !== 'modalUploadFileInput') {
      if (!email) {
        this.showMissingEmailField()
      } else {
        this.clearShowMissingEmailField()
      }
    }
  }

  getLabelAndBlock() {
    const { page_id: label, block } = this.$modalUploadContainer.dataset || {}
    return { label, block }
  }

  fillHomepageEventListeners() {
    const uploadButtons = document.getElementsByClassName('upload__resume-button')

    for (let button of uploadButtons) {
      button.addEventListener('click', e => {
        e.preventDefault()
        this.openModal()

        trackInternalEvent('click_upload_resume_website', {
          ...this.getLabelAndBlock(),
          flow: FLOW,
        })

        const dataset = this.$modalUploadContainer.dataset

        const redirectUrl = dataset.redirectUrl
        if (redirectUrl) {
          return (window.location.href = redirectUrl)
        }
      })
    }
  }

  showMissingEmailField(text) {
    const emailInput = this.$formV1.querySelector('input[name="email"]')
    emailInput.classList.add('error')

    const elements = document.getElementsByClassName('modal_email_span_v1')
    for (let element of elements) {
      element.classList.add('error')
      if (text) element.innerText = text
    }
  }

  clearShowMissingEmailField() {
    const elements = document.getElementsByClassName('modal_email_span_v1')
    for (let element of elements) {
      element.classList.remove('error')
    }

    const emailInput = this.$formV1.querySelector('input[name="email"]')
    emailInput.classList.remove('error')
  }

  showEmailErrorMessage(error, params) {
    this.clearEmailError()

    // Get the email form and input
    const emailForm = document.getElementById('modal_upload_v1_form')
    const emailInput = emailForm.querySelector('input[name="email"]')

    const errorContainer = emailForm.querySelector(
      `.upload-modal__email-error.${error}-error-container`,
    )
    emailInput.classList.add('error')

    // Show the error container
    errorContainer.style.display = 'block'

    if (error === 'email_suggestion') {
      const emailSuggestionSpan = errorContainer.querySelector('#email_suggestion')
      emailSuggestionSpan.innerHTML = ` <b>${params.suggestion}</b>?`
    }
  }

  clearEmailError() {
    const emailInput = this.$formV1.querySelector('input[name="email"]')
    emailInput.classList.remove('error')
    // Remove any existing error message
    const errorContainers = document.getElementsByClassName('upload-modal__email-error')
    for (const container of errorContainers) {
      container.style.display = 'none'
    }
  }
}

ready(function () {
  new UploadModalSettings()
})
